/**
 * Typed GA4 event helpers (#256). Thin wrappers over {@link gaEvent} that build
 * GA4-recommended event payloads, so call sites stay clean and the event schema
 * lives in one place. All inherit the consent gate + no-op-when-unconfigured
 * behavior of `gaEvent`.
 *
 * Event names use GA4 recommendations where they exist (`search`, `view_item`,
 * `select_item`) so GA4's built-in reports populate without custom config;
 * `view_caliber` is a custom event for the caliber landing pages.
 */

import { gaEvent } from './ga';

/** A search was run (GA4 recommended `search`). Surfaces "popular searches". */
export function trackSearch(params: {
  searchTerm?: string;
  caliber?: string;
  results?: number;
}): void {
  gaEvent('search', {
    search_term: params.searchTerm,
    caliber: params.caliber,
    results: params.results,
  });
}

/** A product detail page was viewed (GA4 recommended `view_item`). */
export function trackViewItem(params: {
  id: string;
  name?: string;
  brand?: string;
  caliber?: string;
}): void {
  gaEvent('view_item', {
    item_id: params.id,
    item_name: params.name,
    item_brand: params.brand,
    item_category: params.caliber,
  });
}

/** A caliber landing page was viewed (custom). Surfaces "popular calibers". */
export function trackViewCaliber(params: { caliber: string; inStockCount?: number }): void {
  gaEvent('view_caliber', {
    caliber: params.caliber,
    in_stock_count: params.inStockCount,
  });
}

/** A user clicked through to a retailer (GA4 recommended `select_item`). */
export function trackOutboundClick(params: {
  listingId: string;
  productName?: string;
  retailer?: string;
  caliber?: string;
}): void {
  gaEvent('select_item', {
    item_list_name: 'outbound_retailer',
    item_id: params.listingId,
    item_name: params.productName,
    retailer: params.retailer,
    item_category: params.caliber,
  });
}
